import {FC, useMemo} from 'react';
import {
  ReactComponent as CancelledTaskIcon
} from '../../assets/images/cancelled-task.svg';
import {AlertSettingsProps} from '../settings/alert_alarm/types';
import {ProcessCode, TaskDetailsProps} from './types';
import LiveTimeProgressBar
  from '../common/time_progress_bar/LiveTimeProgressBar';
import {getDatesDifferenceInSeconds} from '../../hooks/useTimer';
import {calculateAndFormatTimeMMSS} from './helpers';
import {useHistory} from 'react-router-dom';
import tasksRoutes from '../tasks/routes';
import classNames from 'classnames';
import {useAppSelector} from '../../store/hooks';
import permissionsInfo from '../../constants/permissionsInfo';
import {currentUserProps} from '../../store/currentUser/currentUser.slice';
import {RootState} from '../../store/store';
import useUserPermissions from '../../hooks/useUserPermissions';

const labels = {
  goal: 'GOAL',
  completedIn: 'COMPLETED IN',
  assignee: 'TEAM MEMBER'
};

type TaskDetailsElementProps = {
  taskItem: TaskDetailsProps,
  taskAlertData: Nullable<AlertSettingsProps>,
  taskNumber: number
}

const TaskDetailsElement: FC<TaskDetailsElementProps> =({
  taskItem,
  taskAlertData,
  taskNumber}) => {
  const history = useHistory();
  const currentUser: currentUserProps = useAppSelector(
      (state: RootState) => state.user
  );
  const {groups: userGroups = []} = currentUser || {};

  const {
    assignee,
    completedAt,
    startedAt,
    status,
    targetTime,
    type,
    process,
    groups
  } = taskItem;

  const goalTime = useMemo(() => {
    return calculateAndFormatTimeMMSS(targetTime);
  }, [targetTime]);

  const completedInTime = useMemo(() => {
    const calculatedGoalTimeInSeconds = startedAt && completedAt ?
      getDatesDifferenceInSeconds(startedAt, completedAt) : null;
    return calculateAndFormatTimeMMSS(calculatedGoalTimeInSeconds);
  }, [startedAt, completedAt]);

  const hasTaskViewPermission =
  useUserPermissions(permissionsInfo.tasks.view);

  const hasTaskGroupViewPermission =
  useUserPermissions(permissionsInfo.tasks.group.view);

  const hasPermission = hasTaskViewPermission ||
  (hasTaskGroupViewPermission && !!(groups?.filter(
      (group) => userGroups?.includes(group.slug)
  ).length));

  return (
    <div
      onClick={status?.code === ProcessCode.RUNNING && hasPermission ?
        () => history.push(tasksRoutes.item(process?.id)) : () => {}}
      className={classNames('running-processes-element',
          {'running': status?.code === ProcessCode.RUNNING},
          {'cursor-pointer': hasPermission && status?.code === ProcessCode.RUNNING},
          {'cancelled': status?.code === ProcessCode.CANCELLED})}
    >
      <div className="task-name">
        <span className="task-name__number">{`${taskNumber}.`}</span>
        <span className="task-name__description">{type.description}</span>
      </div>
      {status?.code === ProcessCode.CANCELLED ?
        (<CancelledTaskIcon height={25} width={25}/>) :
        (<>
          <div className="value-with-heading">
            <span className="heading">{labels.goal}</span>
            <span className="value">{goalTime}</span>
          </div>
          <div className="value-with-heading">
            <span className="heading">{labels.completedIn}</span>
            <span className="value">{completedInTime}</span>
          </div>
          <div className="value-with-heading">
            <span className="heading">{labels.assignee}</span>
            <span className="value">{
              assignee && `${assignee.firstName} ${assignee.lastName}`
            }</span>
          </div>
          <LiveTimeProgressBar
            startedAt={startedAt}
            completedAt={completedAt}
            targetTime={targetTime}
            alertSettings={taskAlertData}
            isCurrentlyRunning={status?.code === ProcessCode.RUNNING}
            hasIntervals
          />
        </>)}
    </div>
  );
};

export default TaskDetailsElement;
